import { useEffect, useRef } from "react";

export default function AnimatedBackground() {
  const canvasRef = useRef(null);
  const tRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W, H;

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const orb = (x, y, r, cr, cg, cb, a) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${cr},${cg},${cb},${a})`);
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
    };

    const wave = (yBase, amp, freq, phase, cr, cg, cb, a) => {
      ctx.beginPath();
      for (let x = 0; x <= W; x += 3) {
        const y =
          yBase +
          Math.sin(x * freq + phase + tRef.current * 0.35) * amp +
          Math.sin(x * freq * 1.8 + phase * 1.4 + tRef.current * 0.2) * amp * 0.35 +
          Math.cos(x * freq * 0.7 + tRef.current * 0.12) * amp * 0.2;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.lineTo(W, H);
      ctx.lineTo(0, H);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, `rgba(${cr},${cg},${cb},${a * 0.5})`);
      grad.addColorStop(0.5, `rgba(${cr},${cg},${cb},${a})`);
      grad.addColorStop(1, `rgba(${cr},${cg},${cb},${a * 0.4})`);
      ctx.fillStyle = grad;
      ctx.fill();
    };

    const ribbon = (ox, oy, len, angle, cr, cg, cb) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const grad = ctx.createLinearGradient(ox, oy, ox + cos * len, oy + sin * len);
      grad.addColorStop(0, `rgba(${cr},${cg},${cb},0)`);
      grad.addColorStop(0.3, `rgba(${cr},${cg},${cb},0.35)`);
      grad.addColorStop(0.7, `rgba(${cr},${cg},${cb},0.18)`);
      grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.beginPath();
      for (let i = 0; i <= 100; i++) {
        const p = i / 100;
        const t = tRef.current;
        const bx =
          ox + cos * len * p + Math.sin(p * Math.PI * 3 + t * 0.55) * 28 * Math.sin(p * Math.PI);
        const by =
          oy + sin * len * p + Math.cos(p * Math.PI * 2 + t * 0.38) * 14 * Math.sin(p * Math.PI);
        i === 0 ? ctx.moveTo(bx, by) : ctx.lineTo(bx, by);
      }
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2.5 + Math.sin(tRef.current * 0.5) * 1.2;
      ctx.stroke();
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const t = tRef.current;

      // Ambient orbs
      orb(W * 0.12 + Math.sin(t * 0.18) * 35, H * 0.22, 260, 96, 165, 250, 0.04);
      orb(W * 0.88 + Math.cos(t * 0.14) * 45, H * 0.45, 300, 167, 139, 250, 0.035);
      orb(W * 0.5, H * 0.82 + Math.sin(t * 0.1) * 25, 220, 52, 211, 153, 0.025);
      orb(W * 0.72, H * 0.12, 160, 244, 114, 182, 0.02);

      // Waves
      wave(H * 0.91, 85, 0.003, 0, 96, 165, 250, 0.022);
      wave(H * 0.87, 95, 0.004, 2, 167, 139, 250, 0.018);
      wave(H * 0.74, 75, 0.005, 4, 96, 165, 250, 0.013);
      wave(H * 0.63, 105, 0.0032, 1.5, 52, 211, 153, 0.009);

      // Ribbons
      ribbon(
        W * 0.08 + Math.sin(t * 0.25) * 40,
        H * 0.18 + Math.cos(t * 0.18) * 28,
        W * 0.52,
        0.18 + Math.sin(t * 0.09) * 0.08,
        96, 165, 250
      );
      ribbon(
        W * 0.62 + Math.cos(t * 0.22) * 35,
        H * 0.38 + Math.sin(t * 0.16) * 32,
        W * 0.42,
        -0.28 + Math.cos(t * 0.11) * 0.09,
        167, 139, 250
      );
      ribbon(
        W * 0.22 + Math.sin(t * 0.19) * 28,
        H * 0.58 + Math.cos(t * 0.14) * 22,
        W * 0.38,
        0.38,
        52, 211, 153
      );

      tRef.current += 0.007;
      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}
